import BaseTextInput from "@/components/form/BaseTextInput";
import { FormItem } from "@/components/form/FormItem";
import RadioGroup from "@/components/form/RadioGroup";
import { PageTitle } from "@/components/layouts/admin/PageTitle";
import { BaseModal } from "@/components/BaseModal";
import { showErrorDialog } from "@/helpers/showErrorDialog";
import { MediaCategory } from "@/interfaces/MediaCategory";
import { MediaCategoryService } from "@/services/media-category.service";
import { MediaContentService } from "@/services/media-content.service";
import { Editor } from "@tinymce/tinymce-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "@/configs/axios";
import { FaTrash } from "react-icons/fa";

interface FormValues {
  title: string;
  riskLevel: string;
  categoryId: string;
  youtubeUrl: string;
  detail: string;
}

const contentService = new MediaContentService();
const categoryService = new MediaCategoryService();

export default function CreateMediaContentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [detail, setDetail] = useState("");
  const [categories, setCategories] = useState<MediaCategory[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<FormValues>();

  useEffect(() => {
    categoryService.listAll().then(setCategories).catch(showErrorDialog);
  }, []);

  useEffect(() => {
    if (!id) return;
    const fetch = async () => {
      Swal.showLoading();
      try {
        const res = await contentService.get(id);
        setValue("title", res.title);
        setValue("riskLevel", res.riskLevel);
        setValue("categoryId", res.categoryId ?? "");
        setValue("youtubeUrl", res.youtubeUrl ?? "");
        setDetail(res.detail ?? "");
        Swal.close();
      } catch (error) {
        Swal.close();
        showErrorDialog(error);
      }
    };
    fetch();
  }, [id]);

  const handleDelete = async () => {
    setDeleteModalOpen(false);
    Swal.showLoading();
    try {
      await contentService.remove(id);
      Swal.close();
      await Swal.fire("สำเร็จ", "ลบรายการเรียบร้อย", "success");
      navigate("/admin/media-contents");
    } catch (error) {
      Swal.close();
      showErrorDialog(error);
    }
  };

  const onSubmit = async (values: FormValues) => {
    Swal.showLoading();
    try {
      const body = {
        ...values,
        detail,
        categoryId: values.categoryId || undefined,
        youtubeUrl: values.youtubeUrl || undefined,
      };
      if (id) {
        await contentService.update(id, body);
      } else {
        await contentService.create(body);
      }
      Swal.close();
      await Swal.fire("สำเร็จ", "บันทึกสำเร็จ", "success");
      navigate("/admin/media-contents");
    } catch (error) {
      Swal.close();
      showErrorDialog(error);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="w-full">
        <PageTitle title={id ? "แก้ไขสื่อ" : "เพิ่มสื่อ"} />
        <form className="card" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-6 p-6 py-10 card-content">
            <FormItem label="หัวข้อ" isVertical error={errors.title?.message} required>
              <BaseTextInput
                control={control}
                name="title"
                error={errors.title?.message}
              />
            </FormItem>

            <FormItem label="ความเสี่ยง" isVertical error={errors.riskLevel?.message} required>
              <Controller
                name="riskLevel"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    isVertical
                    name="riskLevel"
                    options={[
                      { label: "กลุ่มเสี่ยงน้อย", value: "LOW_RISK" },
                      { label: "กลุ่มเสี่ยงสูง", value: "HIGH_RISK" },
                    ]}
                    onChange={field.onChange}
                    value={field.value as any}
                    error={errors.riskLevel?.message}
                  />
                )}
              />
            </FormItem>

            <FormItem label="หมวดหมู่" isVertical>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <select
                    className="select select-bordered w-full max-w-xs"
                    value={field.value ?? ""}
                    onChange={field.onChange}>
                    <option value="">-- ไม่ระบุหมวดหมู่ --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                )}
              />
            </FormItem>

            <FormItem label="ลิงก์ YouTube" isVertical>
              <BaseTextInput
                control={control}
                name="youtubeUrl"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </FormItem>

            <FormItem label="รายละเอียด" isVertical>
              <Editor
                apiKey={import.meta.env.VITE_APP_TINY_MCE_API_KEY}
                onInit={(_, editor) => (editorRef.current = editor)}
                init={{
                  plugins:
                    "anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount",
                  toolbar:
                    "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat",
                  images_upload_handler: async (blobInfo): Promise<string> => {
                    const data = new FormData();
                    data.append("file", blobInfo.blob());
                    try {
                      const res = await axios.post("/files/upload", data, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                      return `${import.meta.env.VITE_APP_API_HOST}/api/files/${res.data?.url}`;
                    } catch (error) {
                      showErrorDialog(error);
                      return "";
                    }
                  },
                }}
                value={detail}
                onEditorChange={(val) => setDetail(val)}
              />
            </FormItem>

            <div className="flex items-center gap-3">
              <button type="submit" className="btn btn-primary min-w-[120px]">
                บันทึก
              </button>
              {id && (
                <button
                  type="button"
                  className="btn btn-error min-w-[120px]"
                  onClick={() => setDeleteModalOpen(true)}>
                  <FaTrash /> ลบ
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <BaseModal
        visible={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="ยืนยันการลบ"
        width={400}>
        <div className="flex flex-col gap-6">
          <p>ต้องการลบรายการนี้ใช่หรือไม่? การลบจะไม่สามารถกู้คืนได้</p>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              className="btn"
              onClick={() => setDeleteModalOpen(false)}>
              ยกเลิก
            </button>
            <button
              type="button"
              className="btn btn-error"
              onClick={handleDelete}>
              ยืนยันการลบ
            </button>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
